// src/components/RabbitMQDetailsModal.jsx
import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Box, Button, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, Stack, Card, CardContent,
  IconButton, Grid, CircularProgress
} from '@mui/material';
import {
  Close, Refresh, Hub, Queue, Link,
  CheckCircle, Error, Warning
} from '@mui/icons-material';

const RabbitMQDetailsModal = ({ open, onClose, rabbitmqStatus, onRefresh, loading }) => {
  if (!rabbitmqStatus) return null;

  const { overview, stats, message, status, timestamp, analysis } = rabbitmqStatus;

  const renderStatusChip = (status) => {
    const config = {
      connected: { icon: <CheckCircle />, color: 'success', label: 'Доступен' },
      error: { icon: <Error />, color: 'error', label: 'Ошибка' },
      unhealthy: { icon: <Warning />, color: 'warning', label: 'Проблемы' }
    }[status] || { icon: <Warning />, color: 'default', label: 'Неизвестно' };

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        variant="filled"
        size="small"
      />
    );
  };

  const renderOverview = () => {
    if (!overview) return null;

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Hub />
          Общая информация
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Менеджер очередей
                </Typography>
                {overview.queue_totals && (
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <strong>Сообщения:</strong> {overview.queue_totals.messages || 0}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Готовы:</strong> {overview.queue_totals.messages_ready || 0}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Неподтверждены:</strong> {overview.queue_totals.messages_unacknowledged || 0}
                    </Typography>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Объекты системы
                </Typography>
                {overview.object_totals && (
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <strong>Очереди:</strong> {overview.object_totals.queues || 0}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Подключения:</strong> {overview.object_totals.connections || 0}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Каналы:</strong> {overview.object_totals.channels || 0}
                    </Typography>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderQueues = () => {
    if (!stats || !stats.queues || !Array.isArray(stats.queues)) {
      return (
        <Alert severity="info">
          Информация об очередях недоступна
        </Alert>
      );
    }

    const queues = stats.queues;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Queue />
          Очереди ({queues.length})
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>Имя очереди</strong></TableCell>
                <TableCell align="right"><strong>Сообщения</strong></TableCell>
                <TableCell align="right"><strong>Готовы</strong></TableCell>
                <TableCell align="right"><strong>Неподтверждены</strong></TableCell>
                <TableCell><strong>Состояние</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queues.map((queue, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {queue.name || 'unnamed'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={queue.messages || 0} 
                      size="small"
                      variant="outlined"
                      color={queue.messages > 0 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">{queue.messages_ready || 0}</TableCell>
                  <TableCell align="right">{queue.messages_unacknowledged || 0}</TableCell>
                  <TableCell>
                    <Chip 
                      label={queue.state || 'idle'} 
                      size="small"
                      color={queue.state === 'running' ? 'success' : 'default'}
                      variant="filled"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderConnections = () => {
    if (!stats || !stats.connections || !Array.isArray(stats.connections)) {
      return null;
    }

    const connections = stats.connections;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link />
          Подключения ({connections.length})
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>Имя</strong></TableCell>
                <TableCell><strong>Пользователь</strong></TableCell>
                <TableCell><strong>Состояние</strong></TableCell>
                <TableCell><strong>Протокол</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {connections.slice(0, 10).map((conn, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {conn.name || conn.client_properties?.connection_name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>{conn.user || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={conn.state || 'unknown'} 
                      size="small"
                      color={conn.state === 'running' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{conn.protocol || 'N/A'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {connections.length > 10 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Показано 10 из {connections.length} подключений
          </Typography>
        )}
      </Box>
    );
  };

  const renderAnalysis = () => {
    if (!analysis) return null;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          📈 Анализ состояния
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Очереди
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">Всего: {analysis.queue_health?.total || 0}</Typography>
                  <Typography variant="body2">С сообщениями: {analysis.queue_health?.with_messages || 0}</Typography>
                  <Typography variant="body2">Активные: {analysis.queue_health?.running || 0}</Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Подключения
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">Всего: {analysis.connection_health?.total || 0}</Typography>
                  <Typography variant="body2">Активные: {analysis.connection_health?.running || 0}</Typography>
                  <Typography variant="body2">Заблокированы: {analysis.connection_health?.blocked || 0}</Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Ноды
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">Всего: {analysis.node_health?.total || 0}</Typography>
                  <Typography variant="body2">Здоровые: {analysis.node_health?.healthy || 0}</Typography>
                  <Typography variant="body2">Проблемы: {analysis.node_health?.unhealthy || 0}</Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Hub />
          <Typography variant="h5" fontWeight="bold">
            Детальная информация RabbitMQ
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {renderStatusChip(status)}
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Статус и сообщение */}
          <Alert 
            severity={status === 'connected' ? 'success' : 'error'}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={onRefresh}
                startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
                disabled={loading}
              >
                {loading ? 'Обновление...' : 'Обновить'}
              </Button>
            }
          >
            <Typography variant="body2">
              <strong>Статус:</strong> {status} | {message}
            </Typography>
            {timestamp && (
              <Typography variant="caption" display="block">
                Обновлено: {new Date(timestamp).toLocaleString()}
              </Typography>
            )}
          </Alert>

          {/* Общая информация */}
          {renderOverview()}

          {/* Анализ состояния */}
          {renderAnalysis()}

          {/* Очереди */}
          {renderQueues()}

          {/* Подключения */}
          {renderConnections()}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
        <Button onClick={onClose} startIcon={<Close />}>
          Закрыть
        </Button>
        <Button 
          variant="contained" 
          onClick={onRefresh}
          startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
          disabled={loading}
        >
          {loading ? 'Обновление...' : 'Обновить данные'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RabbitMQDetailsModal;